import type {
  CartByIdQuery,
  CollectionQuery,
  FavouriteProductsQuery,
  ProductQuery
} from '../../types/storefront.generated';

export type CollectionDetail = CollectionQuery['collection'];

/** A product card (the ProductCard fragment, used by the collection and products queries). */
export type ProductListItem =
  NonNullable<CollectionDetail>['products']['nodes'][number];

export type ProductDetail = ProductQuery['product'];

export type Variant = NonNullable<ProductDetail>['variants']['nodes'][number];

export type Option = NonNullable<ProductDetail>['options'][number];

/** Custom type that extends a product option with a display name. */
export type OptionWithDisplayName =
  NonNullable<ProductDetail>['options'][number] & {
    displayName: string;
  };

export type Cart = NonNullable<CartByIdQuery['cart']>;

export type CartLine = Cart['lines']['edges'][number]['node'];

export type FavouriteProduct = NonNullable<
  FavouriteProductsQuery['nodes'][number]
>;

/** What the cart indicator shows in its "added to cart" notification. */
export interface LastItemAdded {
  productTitle: string;
  variantTitle: string;
  price: string;
  imageUrl?: string;
}

/**
 * Runtime copy of the Storefront API's ProductSortKeys enum: the generated one (types/storefront.types.d.ts) is
 * declaration only, so it can't be used as a value (e.g. in a validator).
 *
 * Sorting options for the [`products`](https://shopify.dev/docs/api/storefront/current/queries/products) query. Supports sorting products by criteria such as best-selling and price, and by product attributes such as type, and vendor.
 *
 * > Note: Use the [`RELEVANCE`](https://shopify.dev/docs/api/storefront/current/enums/ProductSortKeys#enums-RELEVANCE) key only when a search query is specified.
 *
 */
export enum ProductSortKeys {
  /** Sort by the `best_selling` value. */
  BestSelling = 'BEST_SELLING',
  /** Sort by the `created_at` value. */
  CreatedAt = 'CREATED_AT',
  /** Sort by the `id` value. */
  Id = 'ID',
  /** Sort by the `price` value. */
  Price = 'PRICE',
  /** Sort by the `product_type` value. */
  ProductType = 'PRODUCT_TYPE',
  /**
   * Sort by relevance to the search terms when the `query` parameter is specified on the connection.
   * Don't use this sort key when no search query is specified.
   *
   */
  Relevance = 'RELEVANCE',
  /** Sort by the `title` value. */
  Title = 'TITLE',
  /** Sort by the `updated_at` value. */
  UpdatedAt = 'UPDATED_AT',
  /** Sort by the `vendor` value. */
  Vendor = 'VENDOR'
}

/**
 * Runtime copy of the Storefront API's ProductCollectionSortKeys enum (see ProductSortKeys).
 *
 * Sort options for products within a [`Collection`](https://shopify.dev/docs/api/storefront/current/objects/Collection).
 * Without a sort key, the products are in the collection's sort order set in the Shopify admin.
 */
export enum ProductCollectionSortKeys {
  /** Sort by the `best-selling` value. */
  BestSelling = 'BEST_SELLING',
  /** Sort by the `collection-default` value. */
  CollectionDefault = 'COLLECTION_DEFAULT',
  /** Sort by the `created` value. */
  Created = 'CREATED',
  /** Sort by the `id` value. */
  Id = 'ID',
  /** Sort by the `manual` value. */
  Manual = 'MANUAL',
  /** Sort by the `price` value. */
  Price = 'PRICE',
  /**
   * Sort by relevance to the search terms when the `query` parameter is specified on the connection.
   * Don't use this sort key when no search query is specified.
   */
  Relevance = 'RELEVANCE',
  /** Sort by the `title` value. */
  Title = 'TITLE'
}
